import React, { useState } from 'react';
import { Key, Copy, Eye, EyeOff, Trash2, Plus, Clock } from 'lucide-react';
import { PageHeader, PageShell, SectionCard, Badge } from '../components/PeecUI';

interface ApiKeysPageProps {
  workspaceId: string;
}

const initialKeys = [
  { id: 'key_1', name: '生产环境主密钥', prefix: 'pk_live_', suffix: 'x8K2...Qr9f', created: '2026-01-15', lastUsed: '2小时前', status: 'active', scope: '全部权限' },
  { id: 'key_2', name: '测试环境密钥', prefix: 'pk_test_', suffix: 'mN4P...Wv3e', created: '2026-03-08', lastUsed: '昨天', status: 'active', scope: '只读' },
  { id: 'key_3', name: '数据导出专用', prefix: 'pk_live_', suffix: 'aB7Y...Lq2k', created: '2026-04-22', lastUsed: '5天前', status: 'active', scope: '数据导出' },
];

const ApiKeysPage: React.FC<ApiKeysPageProps> = () => {
  const [keys, setKeys] = useState(initialKeys);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  const toggleReveal = (id: string) => setRevealed(r => ({ ...r, [id]: !r[id] }));

  const copyKey = (id: string) => {
    navigator.clipboard.writeText(`${keys.find(k => k.id === id)?.prefix}••••••••`);
  };

  const revokeKey = (id: string) => {
    setKeys(prev => prev.map(k => k.id === id ? { ...k, status: 'revoked' } : k));
  };

  return (
    <PageShell>
      <PageHeader title="API 密钥" description="管理用于访问 PEEC API 的密钥凭证" />

      <div className="mb-5 flex items-center justify-between">
        <div className="text-sm text-slate-500">{keys.filter(k => k.status === 'active').length} 个活跃密钥</div>
        <button className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors">
          <Plus size={15} /> 生成新密钥
        </button>
      </div>

      <SectionCard title="API 密钥列表" subtitle="保管好您的密钥，不要在公开渠道分享">
        <div className="divide-y divide-slate-100">
          {keys.map((key) => (
            <div key={key.id} className="flex items-center gap-4 p-4 hover:bg-slate-50/70 transition-colors">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <Key size={16} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-sm text-slate-900">{key.name}</span>
                  <Badge tone={key.status === 'active' ? 'green' : 'slate'}>{key.status === 'active' ? '活跃' : '已撤销'}</Badge>
                  <Badge tone="blue">{key.scope}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="font-mono">{key.prefix}{revealed[key.id] ? '生产密钥（仅限内部）' : key.suffix}</span>
                  <span className="flex items-center gap-1"><Clock size={10} /> 最近使用：{key.lastUsed}</span>
                  <span>创建于 {key.created}</span>
                </div>
              </div>
              {key.status === 'active' && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => toggleReveal(key.id)}
                    aria-label={revealed[key.id] ? '隐藏密钥' : '显示密钥'}
                    className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-violet-400 outline-none"
                  >
                    {revealed[key.id] ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                  <button
                    onClick={() => copyKey(key.id)}
                    aria-label="复制密钥"
                    className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-violet-400 outline-none"
                  >
                    <Copy size={15} />
                  </button>
                  <button
                    onClick={() => revokeKey(key.id)}
                    aria-label="撤销密钥"
                    className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors focus-visible:ring-2 focus-visible:ring-rose-400 outline-none"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard className="mt-5" title="API 使用量" subtitle="过去 30 天的调用统计">
        <div className="grid gap-4 p-4 sm:grid-cols-3">
          {[
            { label: '总调用次数', value: '94,280', sub: '本月' },
            { label: '错误率', value: '0.12%', sub: '低于基线' },
            { label: '平均响应时间', value: '124ms', sub: 'P99: 380ms' },
          ].map((item) => (
            <div key={item.label} className="rounded-lg bg-slate-50 p-4">
              <div className="text-xs text-slate-500 mb-1">{item.label}</div>
              <div className="text-2xl font-bold text-slate-950">{item.value}</div>
              <div className="text-xs text-slate-400 mt-0.5">{item.sub}</div>
            </div>
          ))}
        </div>
      </SectionCard>
    </PageShell>
  );
};

export default ApiKeysPage;
